/**
 * Batch Job History Page
 * Paginated execution history with filtering
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn, formatDate, formatDuration, formatNumber } from '@/lib/utils'
import { getBatchJobHistory } from '../queries/batchJobs'
import { JobStatusBadge } from '../components/JobStatusBadge'
import { TriggerTypeBadge } from '../components/TriggerTypeBadge'
import type { BatchJobHistoryResponse } from '../types'

const JOB_OPTIONS = [
  { value: '', label: 'All Jobs' },
  { value: 'CustomerMetricsSync', label: 'Customer Metrics Sync' },
  { value: 'AccountBaselineSync', label: 'Account Baseline Sync' },
  { value: 'DailyEventMetrics', label: 'Daily Event Metrics' },
  { value: 'DomainExclusionListCount', label: 'Domain Exclusion List Count' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILED', label: 'Failed' },
]

const LIMIT_OPTIONS = [
  { value: '20', label: '20 per page' },
  { value: '50', label: '50 per page' },
  { value: '100', label: '100 per page' },
]

function formatSchedule(cron: string | null | undefined): string {
  if (!cron) return 'Manual only'
  const map: Record<string, string> = {
    '0 0 */2 * * *': 'Every 2 hours',
    '0 0 2 * * *': 'Daily 2:00 AM UTC',
    '0 0 5 * * *': 'Daily 5:00 AM UTC',
    '0 */30 * * * *': 'Every 30 min',
  }
  return map[cron] ?? cron
}

// Suppress unused warning — formatSchedule is available for future use in detail columns
void formatSchedule

export function BatchJobHistoryPage() {
  const navigate = useNavigate()

  const [jobName, setJobName] = useState('')
  const [status, setStatus] = useState('')
  const [accountIdInput, setAccountIdInput] = useState('')
  const [limit, setLimit] = useState(20)
  const [offset, setOffset] = useState(0)

  const parsedAccountId = accountIdInput !== '' ? parseInt(accountIdInput, 10) : undefined

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<BatchJobHistoryResponse>({
    queryKey: ['batch-jobs', 'history', jobName, status, parsedAccountId, limit, offset],
    queryFn: () =>
      getBatchJobHistory({
        jobName: jobName || undefined,
        accountId: parsedAccountId,
        status: status || undefined,
        limit,
        offset,
      }),
  })

  const executions = data?.executions ?? []
  const total = data?.total ?? 0
  const currentStart = total === 0 ? 0 : offset + 1
  const currentEnd = Math.min(offset + limit, total)
  const hasPrev = offset > 0
  const hasNext = offset + limit < total

  const handleFilterChange = () => {
    setOffset(0)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/batch-jobs">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Execution History</h1>
            <p className="text-gray-500 mt-0.5 text-sm">
              Browse all batch job executions with filtering and pagination.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Job Name</label>
            <Select
              options={JOB_OPTIONS}
              value={jobName}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setJobName(e.target.value)
                handleFilterChange()
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setStatus(e.target.value)
                handleFilterChange()
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Account ID</label>
            <input
              type="number"
              placeholder="Any"
              value={accountIdInput}
              onChange={(e) => {
                setAccountIdInput(e.target.value)
                handleFilterChange()
              }}
              className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Rows per page</label>
            <Select
              options={LIMIT_OPTIONS}
              value={String(limit)}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setLimit(parseInt(e.target.value, 10))
                handleFilterChange()
              }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading && <LoadingState message="Loading execution history..." />}
      {error && (
        <ErrorState
          error={error as Error}
          message="Failed to load execution history"
          onRetry={refetch}
        />
      )}

      {!isLoading && !error && (
        <>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Job</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Account</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Trigger</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Started At</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Duration</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Rows Processed</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {executions.map((exec) => (
                    <tr
                      key={exec.executionId}
                      onClick={() => navigate(`/batch-jobs/${exec.executionId}`)}
                      className="hover:bg-indigo-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {exec.displayName}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {exec.accountId != null ? `Account ${exec.accountId}` : 'All'}
                      </td>
                      <td className="px-4 py-3">
                        <JobStatusBadge status={exec.status} />
                      </td>
                      <td className="px-4 py-3">
                        <TriggerTypeBadge triggerType={exec.triggerType} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {formatDate(exec.startedAt)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 font-mono whitespace-nowrap">
                        {formatDuration(exec.durationMs)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600 font-mono whitespace-nowrap">
                        {exec.rowsProcessed != null ? formatNumber(exec.rowsProcessed) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-red-600 max-w-[200px]">
                        {exec.errorMessage ? (
                          <span
                            className="block truncate"
                            title={exec.errorMessage}
                          >
                            {exec.errorMessage}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {executions.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                        No executions found matching the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                Showing <span className="font-medium">{currentStart}</span>–
                <span className="font-medium">{currentEnd}</span> of{' '}
                <span className="font-medium">{formatNumber(total)}</span>
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrev}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  className={cn(!hasPrev && 'opacity-40 cursor-not-allowed')}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNext}
                  onClick={() => setOffset(offset + limit)}
                  className={cn(!hasNext && 'opacity-40 cursor-not-allowed')}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
